import { useEffectOnce } from 'react-use';

import type { Site } from '@hotjar/site';
import { tracking, getContextByReferrer, ModalId } from '@hotjar/upsell';
import type { Referrer } from '@hotjar/upsell';
import type {
  SubmitCheckoutParams,
  SubmitCheckoutResult,
} from '@hotjar/webapp/domains/revenue/hooks/useUpsellCheckout';
import { createGlobalUpsellModal } from '@hotjar/webapp/domains/revenue/upsell/createGlobalUpsellModal';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { upsellLogger } from '@hotjar/webapp/domains/revenue/utils';
import {
  DatadogActionType,
  DatadogUpsellType,
} from '@hotjar/webapp/domains/revenue/upsell/constants';
import { ErrorBoundary } from '@hotjar/error-handler';
import { logError } from '@hotjar/insights-common/lib/logger';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';

import { PlanSelectModal } from '../PlanSelectModal/PlanSelectModal';
import { ProductSelectUpsellModal } from '../ProductSelectUpsellModal/ProductSelectUpsellModal';
import { MultiProductStep, useMultiProductsState } from './hooks/useMultiProductsState';

interface Props {
  site: Site;
  referrer: Referrer;
}

export const MultiProductWrapper = (
  props: Props &
    GlobalModalProps<SubmitCheckoutParams> & /*
     * The reason we inline these props here and not put them into the `Props` interface is that otherwise we'd get
     * an error in the `upsell` function. We don't want to pass these values from there, but from
     * `createGlobalUpsellModal`.
     */ {
      billingPeriod: BillingPeriod;
      billingPeriodChanged: boolean;
      setBillingPeriod: (value: BillingPeriod) => unknown;
    },
): JSX.Element => {
  const { site, referrer, onSubmit } = props;
  const {
    step,
    goPrevStep,
    goNextStep,
    selectedProductTypes,
    setSelectedProductTypes,
    changeSelectedProductTypes,
    selectedProducts,
    changeSelectedProducts,
    selectedSkus,
  } = useMultiProductsState();

  useEffectOnce(() => {
    tracking.upsellFlowEntered({
      account: site.account,
      entryPoint: referrer,
    });
  });

  const onCancel = () => {
    tracking.upsellFlowExited({ account: site.account });
    // eslint-disable-next-line react/destructuring-assignment
    props.onCancel();
  };

  const onError = (error: unknown) => {
    if (error && error instanceof Error) {
      logError(error);

      upsellLogger?.error('Multi-product modal', {
        upsellType: DatadogUpsellType.MultiProductWrapper,
        action: DatadogActionType.Failure,
        errorMessage: error.message,
      });
    }
  };

  const context = getContextByReferrer(referrer);

  /**
   * Depending on the referrer triggering the upsell, we either use the entire flow (including product and both plan
   * selections) or only parts of it in a contextualized flow. Contextualized flows will typically skip the product
   * selection step, and will only use the plan selection step(s).
   *
   * isMultiProductFlow is true when we couldn't resolve a context using the referrer, or when the resolved context
   * has a modalId of ModalId.MultiProduct. In these cases we want to use the entire multi-product flow.
   */
  const isMultiProductFlow = !context || context?.modalId === ModalId.MultiProduct;

  // Sets the products specified by the upsell context
  useEffectOnce(() => {
    switch (context?.product) {
      case ProductType.Analytics:
        setSelectedProductTypes({
          [ProductType.Analytics]: true,
          [ProductType.Feedback]: false,
        });
        break;
      case ProductType.Feedback:
        setSelectedProductTypes({
          [ProductType.Analytics]: false,
          [ProductType.Feedback]: true,
        });
        break;
      default:
        setSelectedProductTypes({
          [ProductType.Analytics]: true,
          [ProductType.Feedback]: true,
        });
        break;
    }
  });

  return (
    <ErrorBoundary squadSentryTag="upsell" onError={onError}>
      {step === MultiProductStep.ProductSelection && isMultiProductFlow && (
        <ProductSelectUpsellModal
          {...props}
          selectedProductTypes={selectedProductTypes}
          changeSelectedProductTypes={changeSelectedProductTypes}
          goNextStep={goNextStep}
          onCancel={onCancel}
        />
      )}
      {(step === MultiProductStep.PlanSelection || !isMultiProductFlow) && context && (
        <PlanSelectModal
          {...props}
          selectedProducts={selectedProducts}
          changeSelectedProducts={changeSelectedProducts}
          selectedProductTypes={selectedProductTypes}
          goPrevStep={goPrevStep}
          onCancel={onCancel}
          onSubmit={onSubmit as (params: SubmitCheckoutParams) => Promise<SubmitCheckoutResult>}
          referrer={referrer}
          context={context}
          selectedSkus={selectedSkus}
        />
      )}
    </ErrorBoundary>
  );
};

export const openMultiProductsModal = createGlobalUpsellModal<Props, SubmitCheckoutParams, Error>(
  MultiProductWrapper,
);
